import { createStyles, Divider, Drawer, Skeleton, Stack } from '@mantine/core';
import {
	fetchApiGetQueryCharts,
	fetchApiGetQueryMetadata,
} from '@repo/api-codegen';
import { Title } from '@repo/foundations';
import { useSuspenseQueries } from '@tanstack/react-query';
import { useState } from 'react';
import { useFmtSql } from '../../api/hooks/query/useFormatterQuery';
import QueryHistory from '../../pages/QueryPage/components/QueryHistory';
import { QueryAnalytics } from './components/QueryAnalytics';
import { QueryHeader } from './components/QueryHeader';
import { QueryProperties } from './components/QueryProperties';
import { QuerySql } from './components/QuerySql';
import { graphOptions } from './constants';
import { useQueryDrawer } from './hooks/useQueryDrawer';

const useStyles = createStyles((theme) => ({
	drawerBody: {
		width: '100%',
		height: '100%',
		overflowY: 'auto',
		display: 'flex',
		padding: 0,
	},
	wrapper: {
		width: '100%',
		padding: `${theme.spacing.lg} 0 0 ${theme.spacing.lg}`,
		gap: theme.spacing.xl,
	},
	content: {
		overflowY: 'auto',
		scrollbarGutter: 'stable',
		paddingLeft: 0,
		paddingRight: theme.spacing.lg,
		paddingBottom: theme.spacing.lg,
	},
	history: {
		width: '600px',
		paddingBottom: theme.spacing.xl,
	},
}));

export function QueryDrawerController() {
	const { classes, theme } = useStyles();
	const { opened, close, query } = useQueryDrawer();
	const [lookBackDays, setLookBackDays] = useState(30);
	const [analyticsIsOpen, setAnalyticsIsOpen] = useState(false);
	const [selectedGraph, setSelectedGraph] = useState(graphOptions[0]);
	const [sqlModalOpened, setSqlModalOpened] = useState(false);

	const formattedSql = useFmtSql(query?.sql || '', 'sql');

	const suspenseData = useSuspenseQueries({
		queries: [
			{
				queryKey: ['query', 'metadata', query?.id, lookBackDays],
				queryFn: async () => {
					if (!query?.id) return null;
					return await fetchApiGetQueryMetadata({
						pathParams: { queryId: query.id },
						queryParams: { lookback_days: lookBackDays },
					});
				},
			},
			{
				queryKey: ['query', 'charts', query?.id, lookBackDays],
				queryFn: async () => {
					if (!query?.id) return null;
					return await fetchApiGetQueryCharts({
						pathParams: { queryId: query.id },
						queryParams: { lookback_days: lookBackDays },
					});
				},
			},
		],
	});

	const metadata = suspenseData[0]?.data ?? undefined;
	const charts = suspenseData[1]?.data ?? undefined;

	return (
		<Drawer
			classNames={{
				body: classes.drawerBody,
			}}
			size={theme.other.space[160]}
			withinPortal
			position="right"
			opened={opened}
			onClose={close}
			withCloseButton={false}
		>
			{!query && (
				<Stack pr="md" className={classes.wrapper}>
					<Skeleton height={100} />
					{Array.from({ length: 5 }).map((i) => (
						<Skeleton key={`skeleton-${i}`} height={20} />
					))}
					<Skeleton height={400} />
					<Skeleton height={100} />
				</Stack>
			)}
			{query && (
				<Stack className={classes.wrapper}>
					<QueryHeader
						lookBackDays={lookBackDays}
						setLookBackDays={setLookBackDays}
						close={close}
					/>
					<Stack className={classes.content}>
						<QueryProperties query={query} />
						<Divider />
						<QuerySql
							sql={formattedSql || query.sql}
							modalOpened={sqlModalOpened}
							setModalOpened={setSqlModalOpened}
						/>
						<Divider />
						<QueryAnalytics
							charts={charts}
							metadata={metadata}
							isOpen={analyticsIsOpen}
							setIsOpen={setAnalyticsIsOpen}
							selectedGraph={selectedGraph}
							setSelectedGraph={setSelectedGraph}
						/>
						<Divider />
						<Stack spacing="lg" className={classes.history}>
							<Title order={3}>History</Title>
							<QueryHistory
								lookBackDays={lookBackDays}
								queryId={query.id ?? ''}
								fromDrawer
							/>
						</Stack>
					</Stack>
				</Stack>
			)}
		</Drawer>
	);
}
